import React, { useState, useCallback, useEffect, useContext } from 'react';

import { Button, UploadArea, Notification, Spinner } from '@ergeon/core-components';
import { PlusCircleIcon } from '@ergeon/icons/hero/micro';

import { AgentContext } from '../../../../../context-providers/AgentContext';
import { UnifiedDialogEntry } from '../../../../../types';
import { createOptimisticInternalNote } from '../../utils/createOptimisticEntry';

import RichTextInput from './components/RichTextInput';
import { htmlToMarkdown } from './components/RichTextInput/utils/converters/htmlToMarkdown';
import { useCreateInternalNote } from './hooks/useCreateInternalNote';
import { useComposeDraft, serializeAttachments, deserializeAttachments } from './hooks/useComposeDraft';
import { MAX_ATTACHMENTS, MAX_INTERNAL_NOTE_FILE_SIZE } from './constants';
import { useFileUploadPaste } from './hooks/useFileUploadPaste';
import type { Attachment, InternalNoteDraft, SerializableAttachment } from './types';
import { htmlToPlainText } from './utils/htmlUtils';

const LABEL = 'Add Internal Note';
const PLACEHOLDER = 'Type your message...';
const BUTTON_TEXT = 'Add Note';
const BUTTON_LOADING_TEXT = 'Adding...';

type InternalNoteInputProps = {
  customerId: number | undefined;
  onSuccess?: () => void;
  onAddOptimisticMessage?: (entry: UnifiedDialogEntry) => void;
  onUpdateOptimisticMessage?: (optimisticId: string, updates: Partial<UnifiedDialogEntry>) => void;
  onRemoveOptimisticMessage?: (optimisticId: string) => void;
};

const InternalNoteInput: React.FC<InternalNoteInputProps> = ({
  customerId,
  onSuccess,
  onAddOptimisticMessage,
  onUpdateOptimisticMessage: _onUpdateOptimisticMessage,
  onRemoveOptimisticMessage,
}) => {
  const { fullName: agentName } = useContext(AgentContext);
  const [text, setText] = useState(''); // HTML content from RichTextInput
  const [attachments, setAttachments] = useState<Attachment[]>([]);
  const [draftAttachments, setDraftAttachments] = useState<SerializableAttachment[]>([]);

  const { submit, isSubmitting, error } = useCreateInternalNote();

  const onAttachmentsAdded = useCallback((newAttachments: Attachment[]) => {
    setAttachments((prev) => [...prev, ...newAttachments]);
  }, []);

  const { handleFilePaste } = useFileUploadPaste({
    attachmentsCount: attachments.length,
    maxAttachments: MAX_ATTACHMENTS,
    onAttachmentsAdded,
  });

  const getCurrentDraft = useCallback(
    (): Omit<InternalNoteDraft, 'updatedAt'> => ({
      text,
      attachments: draftAttachments,
    }),
    [text, draftAttachments],
  );

  const handleDraftLoaded = useCallback((draft: InternalNoteDraft) => {
    setText(draft.text);
    setAttachments(deserializeAttachments(draft.attachments));
    setDraftAttachments(draft.attachments);
  }, []);

  const isDraftEmpty = useCallback(
    (draft: Omit<InternalNoteDraft, 'updatedAt'>) => !htmlToPlainText(draft.text).trim() && draft.attachments.length === 0,
    [],
  );

  const { saveDraft, clearDraft } = useComposeDraft<InternalNoteDraft>({
    type: 'internalNote',
    customerId,
    getCurrentDraft,
    onDraftLoaded: handleDraftLoaded,
    isDraftEmpty,
  });

  // Serialize attachments when they change
  useEffect(() => {
    serializeAttachments(attachments).then(setDraftAttachments);
  }, [attachments]);

  // Save draft when text or attachments change
  useEffect(() => {
    saveDraft();
  }, [text, draftAttachments, saveDraft]);

  const hasContent = htmlToPlainText(text).trim() || attachments.length > 0;
  const isDisabled = isSubmitting || !hasContent;

  const handleSubmit = useCallback(async() => {
    if (!customerId || (!htmlToPlainText(text).trim() && attachments.length === 0)) {
      return;
    }

    // Convert HTML to markdown for storage
    const markdownText = htmlToMarkdown(text).trim();

    // Create optimistic message
    let optimisticEntry: UnifiedDialogEntry | null = null;
    if (onAddOptimisticMessage) {
      optimisticEntry = createOptimisticInternalNote({
        text: markdownText,
        createdBy: agentName || 'Unknown',
      });
      onAddOptimisticMessage(optimisticEntry);
    }

    const success = await submit({ customerId, text: markdownText, attachments });

    if (success) {
      setText('');
      setAttachments([]);
      clearDraft();
      // Call onSuccess first (triggers refetch)
      await onSuccess?.();
      // Remove optimistic message AFTER API data is loaded
      if (optimisticEntry && onRemoveOptimisticMessage) {
        onRemoveOptimisticMessage(optimisticEntry.optimisticId!);
      }
    } else if (optimisticEntry && onRemoveOptimisticMessage) {
      // Remove optimistic message on failure
      onRemoveOptimisticMessage(optimisticEntry.optimisticId!);
    }
  }, [customerId, text, agentName, attachments, onSuccess, submit, clearDraft, onAddOptimisticMessage, onRemoveOptimisticMessage]);

  const handleKeyDown = useCallback(
    (event: React.KeyboardEvent) => {
      // Cmd + Enter (Mac) or Ctrl + Enter (Windows/Linux) to send
      if (event.key === 'Enter' && (event.metaKey || event.ctrlKey)) {
        event.preventDefault();
        handleSubmit();
      }
    },
    [handleSubmit],
  );

  return (
    <div className="flex flex-col gap-3 w-full shrink-0">
      {error && (
        <Notification mode="embed" type="Error">
          {error}
        </Notification>
      )}

      <RichTextInput
        className="flex-1"
        isDisabled={isSubmitting}
        label={LABEL}
        minRows={3}
        onChange={setText}
        onFilePaste={handleFilePaste}
        onKeyDown={handleKeyDown}
        placeholder={PLACEHOLDER}
        value={text} />

      <div className="flex lg:flex-row flex-col gap-4">
        <UploadArea filesList={attachments} maxFileSize={MAX_INTERNAL_NOTE_FILE_SIZE} maxUploads={MAX_ATTACHMENTS} onChange={setAttachments} />
        <Button
          className="lg:max-w-36" disabled={isDisabled} flavor="primary" isFullWidth
          onClick={handleSubmit}>
          {isSubmitting ? (
            <>
              <Spinner active color="white" size={18} />
              {BUTTON_LOADING_TEXT}
            </>
          ) : (
            <>
              <PlusCircleIcon className="size-5 shrink-0" />
              {BUTTON_TEXT}
            </>
          )}
        </Button>
      </div>
    </div>
  );
};

export default InternalNoteInput;
